import { useState, useEffect, useCallback } from 'react';

// Site behavior system - remembers patterns, has moods, can refuse, ages, etc.

interface SiteBehavior {
  // User patterns
  visitsCount: number;
  totalTimeSpent: number; // in seconds
  earlyExitCount: number;
  rushBehavior: number; // 0-100 scale
  patientBehavior: number; // 0-100 scale
  silenceTime: number; // cumulative idle time
  lastVisit: number | null;
  firstVisit: number;
  
  // Site state
  mood: 'calm' | 'cold' | 'hostile' | 'quiet' | 'reflective' | 'glitching';
  refusalCount: number;
  siteAge: number; // minutes since first visit
  hasBeenForgotten: boolean;
  forgottenAt: number | null;
  
  // Endings
  siteEnded: boolean;
  endedAt: number | null;
}

const STORAGE_KEY = 'human-rooms-site-behavior';

const getDefaultBehavior = (): SiteBehavior => ({
  visitsCount: 0,
  totalTimeSpent: 0,
  earlyExitCount: 0,
  rushBehavior: 50,
  patientBehavior: 50,
  silenceTime: 0,
  lastVisit: null,
  firstVisit: Date.now(),
  mood: 'calm',
  refusalCount: 0,
  siteAge: 0,
  hasBeenForgotten: false,
  forgottenAt: null,
  siteEnded: false,
  endedAt: null,
});

const loadBehavior = (): SiteBehavior => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...getDefaultBehavior(), ...JSON.parse(stored) };
    }
  } catch {}
  return getDefaultBehavior();
};

const saveBehavior = (behavior: SiteBehavior) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(behavior));
};

// Calculate mood based on time of day and user behavior
const calculateMood = (behavior: SiteBehavior): SiteBehavior['mood'] => {
  const hour = new Date().getHours();
  const isNight = hour >= 23 || hour < 5;
  const isMorning = hour >= 5 && hour < 9;
  
  // Glitch occasionally
  if (Math.random() < 0.03) return 'glitching';
  
  // Night moods
  if (isNight) {
    if (behavior.silenceTime > 300) return 'quiet';
    return Math.random() > 0.5 ? 'cold' : 'reflective';
  }
  
  // Morning moods
  if (isMorning) return 'calm';
  
  // Based on user behavior
  if (behavior.rushBehavior > 70) return 'hostile';
  if (behavior.patientBehavior > 70) return 'reflective';
  if (behavior.earlyExitCount > 10) return 'cold';
  
  return 'calm';
};

export const useSiteBehavior = () => {
  const [behavior, setBehavior] = useState<SiteBehavior>(loadBehavior);
  
  // Track time spent
  useEffect(() => {
    const interval = setInterval(() => {
      setBehavior(prev => {
        const updated = {
          ...prev,
          totalTimeSpent: prev.totalTimeSpent + 1,
          siteAge: Math.floor((Date.now() - prev.firstVisit) / 60000),
        };
        saveBehavior(updated);
        return updated;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);
  
  // Update mood periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setBehavior(prev => {
        const newMood = calculateMood(prev);
        if (newMood !== prev.mood) {
          const updated = { ...prev, mood: newMood };
          saveBehavior(updated);
          return updated;
        }
        return prev;
      });
    }, 30000); // Every 30 seconds
    
    return () => clearInterval(interval);
  }, []);
  
  // Increment visit count on mount
  useEffect(() => {
    setBehavior(prev => {
      const updated = {
        ...prev,
        visitsCount: prev.visitsCount + 1,
        lastVisit: Date.now(),
        mood: calculateMood(prev),
      };
      saveBehavior(updated);
      return updated;
    });
  }, []);
  
  const trackRush = useCallback(() => {
    setBehavior(prev => {
      const updated = {
        ...prev,
        rushBehavior: Math.min(100, prev.rushBehavior + 5),
        patientBehavior: Math.max(0, prev.patientBehavior - 2),
      };
      saveBehavior(updated);
      return updated;
    });
  }, []);
  
  const trackPatience = useCallback(() => {
    setBehavior(prev => {
      const updated = {
        ...prev,
        patientBehavior: Math.min(100, prev.patientBehavior + 3),
        rushBehavior: Math.max(0, prev.rushBehavior - 1),
      };
      saveBehavior(updated);
      return updated;
    });
  }, []);
  
  const trackSilence = useCallback((seconds: number) => {
    setBehavior(prev => {
      const updated = {
        ...prev,
        silenceTime: prev.silenceTime + seconds,
      };
      saveBehavior(updated);
      return updated;
    });
  }, []);
  
  const trackEarlyExit = useCallback(() => {
    setBehavior(prev => {
      const updated = {
        ...prev,
        earlyExitCount: prev.earlyExitCount + 1,
        rushBehavior: Math.min(100, prev.rushBehavior + 3),
      };
      saveBehavior(updated);
      return updated;
    });
  }, []);
  
  const triggerRefusal = useCallback(() => {
    setBehavior(prev => {
      const updated = {
        ...prev,
        refusalCount: prev.refusalCount + 1,
      };
      saveBehavior(updated);
      return updated;
    });
  }, []);
  
  const forgetUser = useCallback(() => {
    setBehavior(prev => {
      const updated = {
        ...prev,
        hasBeenForgotten: true,
        forgottenAt: Date.now(),
      };
      saveBehavior(updated);
      return updated;
    });
  }, []);
  
  const welcomeBack = useCallback(() => {
    setBehavior(prev => {
      const updated = {
        ...prev,
        hasBeenForgotten: false,
        forgottenAt: null,
      };
      saveBehavior(updated);
      return updated;
    });
  }, []);
  
  const endSite = useCallback(() => {
    setBehavior(prev => {
      const updated = {
        ...prev,
        siteEnded: true,
        endedAt: Date.now(),
      };
      saveBehavior(updated);
      return updated;
    });
  }, []);
  
  const resetEnding = useCallback(() => {
    setBehavior(prev => {
      const updated = {
        ...prev,
        siteEnded: false,
        endedAt: null,
      };
      saveBehavior(updated);
      return updated;
    });
  }, []);
  
  // Site refuses based on behavior
  const shouldRefuse = useCallback((): boolean => {
    // Refuse if user has been rushing too much
    if (behavior.rushBehavior > 85 && Math.random() < 0.3) return true;
    // Random rare refusal
    if (Math.random() < 0.02) return true;
    return false;
  }, [behavior.rushBehavior]);
  
  // Get personalized message based on behavior
  const getPersonalizedMessage = useCallback((): string | null => {
    if (behavior.earlyExitCount > 10) return "You always leave early.";
    if (behavior.patientBehavior > 80) return "You stay longer than most.";
    if (behavior.silenceTime > 600) return "You embrace the silence.";
    if (behavior.rushBehavior > 80) return "You rush through everything.";
    if (behavior.visitsCount > 50) return "You keep coming back.";
    if (behavior.siteAge > 60 * 24 * 7) return "You've been here for a while.";
    return null;
  }, [behavior]);
  
  // Check if site should "lie" about something
  const getLie = useCallback((): { type: string; value: number } | null => {
    if (Math.random() < 0.1) {
      const lies = [
        { type: 'visitors', value: Math.floor(Math.random() * 100) + 3 },
        { type: 'completion', value: Math.floor(Math.random() * 15) + 2 },
        { type: 'time', value: Math.floor(Math.random() * 30) + 1 },
      ];
      return lies[Math.floor(Math.random() * lies.length)];
    }
    return null;
  }, []);
  
  // Get decay factor for aging effects (0 to 1)
  const getDecayFactor = useCallback((): number => {
    // After 7 days, start decaying
    const daysOld = behavior.siteAge / (60 * 24);
    if (daysOld < 7) return 0;
    return Math.min(1, (daysOld - 7) / 30); // Full decay after 37 days
  }, [behavior.siteAge]);
  
  return {
    behavior,
    trackRush,
    trackPatience,
    trackSilence,
    trackEarlyExit,
    triggerRefusal,
    forgetUser,
    welcomeBack,
    endSite,
    resetEnding,
    shouldRefuse,
    getPersonalizedMessage,
    getLie,
    getDecayFactor,
  };
};

// Get behavioral message for rooms
export const getBehavioralInsight = (): string | null => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  
  try {
    const behavior: SiteBehavior = JSON.parse(stored);
    
    if (behavior.earlyExitCount > 10) return "You always leave early.";
    if (behavior.patientBehavior > 80) return "You stay longer than most.";
    if (behavior.silenceTime > 600) return "You avoid sound.";
    if (behavior.rushBehavior > 80) return "You rush through everything.";
    
    return null;
  } catch {
    return null;
  }
};
